import { Button, Dropdown } from 'antd'
import { DownOutlined, UserAddOutlined } from '@ant-design/icons'
import { useState, useEffect, SyntheticEvent } from 'react'
import { useSchedule } from './ScheduleContext'
import { FilterableEvent, CrewMember } from './scheduleApi'
import { useUpdateScheduledEventCrewLeadMutation } from './scheduleQueries'
import { findCrewMemberByEmail } from './crewModel'
import AvatarGroup from './DayView/AvatarGroup'
import SelectCrewDropdown from './SelectCrewDropdown'
import './EventSelectCrew.css'

type Variant = 'pill' | 'avatar'

type EventForTrigger = Pick<FilterableEvent, 'crewMemberIds' | 'crewForemanEmail'>

type TriggerProps = {
  event: EventForTrigger
  hasCrew: boolean
  color?: string
}

const renderPillTrigger = ({ event, hasCrew }: TriggerProps) => {
  if (!hasCrew) {
    return <Button
      type="text"
      size="small"
      className="event-select-crew__pill event-select-crew__pill--empty"
      icon={<>
        <UserAddOutlined className="event-select-crew__pill-empty-icon" />
        {' '}
        <DownOutlined className="event-select-crew__pill-empty-chevron" />
      </>}
    />
  }
  return <Button
    type="text"
    size="small"
    className="event-select-crew__pill event-select-crew__pill--filled"
    icon={
      <div className="event-select-crew__pill-filled-inner">
        <AvatarGroup crewForemanEmail={event.crewForemanEmail} crewMemberIds={event.crewMemberIds} />
        <DownOutlined className="event-select-crew__pill-filled-chevron" />
      </div>
    }
  />
}

const renderAvatarTrigger = ({ event, hasCrew, color }: TriggerProps) => {
  if (!hasCrew) {
    return <div role="button" className="event-select-crew__avatar--empty">
      <UserAddOutlined />
    </div>
  }
  return <div role="button" className="event-select-crew__avatar--filled">
    <AvatarGroup crewForemanEmail={event.crewForemanEmail} crewMemberIds={event.crewMemberIds} initialsColor={color} />
    <DownOutlined className="event-select-crew__avatar-chevron" style={{ color: color ?? '#ccc' }} />
  </div>
}

type Props = {
  event: Pick<FilterableEvent, 'id' | 'crewMemberIds' | 'crewForemanEmail'>
  variant?: Variant
  color?: string
}

const EventSelectCrew = ({ event, variant = 'pill', color }: Props) => {
  const { crew } = useSchedule()

  const [open, setOpen] = useState(false)
  const [selectedCrew, setSelectedCrew] = useState<CrewMember[]>([])
  const [crewLeadId, setCrewLeadId] = useState<number | null>(null)

  useEffect(() => {
    const crewMembersFromIds = event.crewMemberIds
      ? event.crewMemberIds.split(',')
        .map(id => crew?.find(c => c.id === parseInt(id)))
        .filter((member): member is CrewMember => member !== undefined)
      : []

    const foreman = event.crewForemanEmail
      ? findCrewMemberByEmail(crew, event.crewForemanEmail)
      : null

    const foremanNotInMembers = foreman && !crewMembersFromIds.some(m => m.id === foreman.id)
    const initialSelectedCrew = foremanNotInMembers
      ? [foreman, ...crewMembersFromIds]
      : crewMembersFromIds

    setSelectedCrew(initialSelectedCrew)
    setCrewLeadId(foreman?.id ?? null)
  }, [event.id, event.crewMemberIds, event.crewForemanEmail, crew])

  const updateScheduledEventCrewLeadMutation = useUpdateScheduledEventCrewLeadMutation()

  const handleCrewChange = (values: number[]) => {
    const newSelectedCrew = crew?.filter(member => values.includes(member.id)) || []
    setSelectedCrew(newSelectedCrew)

    if (crewLeadId && !values.includes(crewLeadId)) {
      setCrewLeadId(null)
    }

    if (newSelectedCrew.length === 1) {
      const singleMember = newSelectedCrew[0]
      if (singleMember.userEmail) {
        setCrewLeadId(singleMember.id)
      } else {
        setCrewLeadId(null)
      }
    } else if (!crewLeadId && newSelectedCrew.length > 0) {
      const firstWithEmail = newSelectedCrew.find(member => member.userEmail)
      if (firstWithEmail) {
        setCrewLeadId(firstWithEmail.id)
      }
    }
  }

  const removeCrewMember = (memberId: number) => {
    setSelectedCrew(selectedCrew.filter(member => member.id !== memberId))
    if (crewLeadId === memberId) {
      setCrewLeadId(null)
    }
  }

  const setCrewLead = (memberId: number) => {
    if (crewLeadId === memberId) {
      setCrewLeadId(null)
    } else {
      setCrewLeadId(memberId)
    }
  }

  const hasChanges = () => {
    const currentCrewIds = event.crewMemberIds ? event.crewMemberIds.split(',').map(id => parseInt(id)).sort() : []
    const newCrewIds = selectedCrew.map(c => c.id).sort()
    const currentCrewLeadId = event.crewForemanEmail ? findCrewMemberByEmail(crew, event.crewForemanEmail)?.id ?? null : null

    return crewLeadId !== currentCrewLeadId ||
      JSON.stringify(currentCrewIds) !== JSON.stringify(newCrewIds)
  }

  const handleApply = () => {
    const crewLeadEmail = crew?.find(c => c.id === crewLeadId)?.userEmail ?? null
    const crewMemberIds = selectedCrew.map(c => c.id).join(',')

    updateScheduledEventCrewLeadMutation.mutate({
      eventId: event.id,
      crewLeadEmail: crewLeadEmail,
      crewMemberIds: crewMemberIds
    }, {
      onSuccess: () => {
        setOpen(false)
      }
    })
  }

  const hasValidCrewMembers = event.crewMemberIds && event.crewMemberIds.split(',').some(id => crew?.find(c => c.id === parseInt(id)))
  const hasValidCrewLead = event.crewForemanEmail !== null && findCrewMemberByEmail(crew, event.crewForemanEmail)
  const hasCrew = !!(hasValidCrewMembers || hasValidCrewLead)

  const stopTriggerPropagation = (e: SyntheticEvent) => {
    e.stopPropagation()
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      const trap = (e: MouseEvent) => {
        e.stopPropagation()
        document.removeEventListener('click', trap, true)
      }
      document.addEventListener('click', trap, true)
      setTimeout(() => document.removeEventListener('click', trap, true), 100)
    }
  }

  const trigger = variant === 'pill'
    ? renderPillTrigger({ event, hasCrew })
    : renderAvatarTrigger({ event, hasCrew, color })

  return (
    <div
      style={{ display: 'flex', alignItems: 'center' }}
      onClick={stopTriggerPropagation}
      onPointerDown={stopTriggerPropagation}
      onMouseDown={stopTriggerPropagation}
    >
      <Dropdown
        popupRender={() =>
          <SelectCrewDropdown
            crew={crew || []}
            selectedCrew={selectedCrew}
            handleCrewChange={handleCrewChange}
            handleApply={handleApply}
            hasChanges={hasChanges() || updateScheduledEventCrewLeadMutation.isLoading}
            crewLeadId={crewLeadId}
            setCrewLead={setCrewLead}
            removeCrewMember={removeCrewMember}
          />
        }
        trigger={['click']}
        placement="bottomRight"
        open={open}
        onOpenChange={handleOpenChange}
      >
        {trigger}
      </Dropdown>
    </div>
  )
}

export default EventSelectCrew
